// Mints the dedicated, scope-limited Collabora (WOPI) token. The session token never reaches the iframe.

import { authorizedFetch } from 'services/auth/api-fetch';
import { wopiTokenUrl } from 'services/auth/constants';

export type WopiToken = {
  accessToken: string;
  // Absolute epoch-ms expiry, exactly as returned by the server.
  ttl: number;
};

export type WopiMintErrorCode = 'session' | 'unauthorized' | 'not_found' | 'rate_limited' | 'network' | 'unknown';

export class WopiMintError extends Error {
  readonly code: WopiMintErrorCode;

  readonly status?: number;

  constructor(code: WopiMintErrorCode, status?: number) {
    super(`WOPI token mint failed: ${code}${status === undefined ? '' : ` (${status})`}`);
    this.name = 'WopiMintError';
    this.code = code;
    this.status = status;
  }
}

type MintBody = { access_token?: unknown; access_token_ttl?: unknown };

const statusToCode = (status: number): WopiMintErrorCode => {
  if (status === 401) return 'unauthorized';
  if (status === 404) return 'not_found';
  if (status === 429) return 'rate_limited';
  return 'unknown';
};

const mint = async (fileId: string): Promise<WopiToken> => {
  let response: Response;
  try {
    // authorizedFetch reuses the single-flight session refresh (getValidAccessToken) and retries one 401 through it.
    response = await authorizedFetch(wopiTokenUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ fileId }),
    });
  } catch (error) {
    // A rejection from the token gate (degraded / logged out) is not a transport failure.
    throw new WopiMintError(error instanceof TypeError ? 'network' : 'session');
  }
  if (response.status !== 200) throw new WopiMintError(statusToCode(response.status), response.status);

  let body: MintBody;
  try {
    body = await response.json();
  } catch {
    throw new WopiMintError('unknown', response.status);
  }
  const { access_token: accessToken, access_token_ttl: ttl } = body;
  if (typeof accessToken !== 'string' || accessToken === '' || typeof ttl !== 'number' || !Number.isFinite(ttl)) {
    throw new WopiMintError('unknown', response.status);
  }
  return { accessToken, ttl };
};

const inFlight = new Map<string, Promise<WopiToken>>();

// Single-flight per file: concurrent callers (re-render, repeated warnings) share ONE POST /wopi/token.
export const mintWopiToken = (fileId: string): Promise<WopiToken> => {
  const pending = inFlight.get(fileId);
  if (pending) return pending;
  const promise = mint(fileId).finally(() => {
    inFlight.delete(fileId);
  });
  inFlight.set(fileId, promise);
  return promise;
};
